// @ts-nocheck
import { FC, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { isNotEmpty } from '../../../../../_dashboard/helpers';
import { PaymentMethod } from '../core/_models';
import clsx from 'clsx';
import { useListView } from '../core/ListViewProvider';
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading';
import { useQueryResponse } from '../core/QueryResponseProvider';
import { createOne, updateOne } from '../../../../services/common_requests';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

type Props = {
  isPaymentMethodLoading: boolean;
  PaymentMethod: PaymentMethod;
};

const editPaymentMethodSchema = Yup.object().shape({
  code: Yup.string().required('Code is required'),
});

const PaymentMethodEditModalForm: FC<Props> = ({ PaymentMethod, isPaymentMethodLoading }) => {
  const { itemIdForUpdate, setItemIdForUpdate } = useListView();
  const { refetchData } = useQueryResponse();
  const intl = useIntl();
  const [paymentMethodForEdit] = useState<PaymentMethod>({ ...PaymentMethod });

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) refetchData();
    setItemIdForUpdate(undefined);
  };

  const formik = useFormik({
    initialValues: paymentMethodForEdit,
    validationSchema: editPaymentMethodSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const isUpdate = isNotEmpty(itemIdForUpdate);
      const payload = isUpdate
        ? {
            ...(values.code !== paymentMethodForEdit.code ? { code: values.code } : {}),
            ...(values.description !== paymentMethodForEdit.description
              ? { description: values.description }
              : {}),
            ...(values.isActive !== paymentMethodForEdit.isActive
              ? { isActive: values.isActive }
              : {}),
            ...(values.isRefundable !== paymentMethodForEdit.isRefundable
              ? { isRefundable: values.isRefundable }
              : {}),
          }
        : {
            code: values.code,
            description: values.description,
            isActive: values.isActive,
            isRefundable: values.isRefundable,
          };
      try {
        const response = isUpdate
          ? await updateOne(payload, `/payment-methods/${itemIdForUpdate}`)
          : await createOne(payload, '/payment-methods');

        if (response?.payload?.code === 2021 || response?.payload?.code === 201) {
          toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.SUCCESSFULLY_UPDATED' }));
          cancel(true);
        } else {
          toast.error(response?.payload?.message || intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION' }));
        }
      } catch (error) {
        toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION' }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <form className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column scroll-y me-n15 pe-6'>
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>Code</label>
            <input
              {...formik.getFieldProps('code')}
              className={clsx('form-control', { 'is-invalid': formik.touched.code && formik.errors.code })}
              disabled={formik.isSubmitting || isPaymentMethodLoading}
            />
          </div>
          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6 mb-2'>Description</label>
            <input
              {...formik.getFieldProps('description')}
              className='form-control'
              disabled={formik.isSubmitting || isPaymentMethodLoading}
            />
          </div>
          <div className='form-check form-switch mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={formik.values.isActive}
              onChange={() => formik.setFieldValue('isActive', !formik.values.isActive)}
            />
            <label className='form-check-label'>Active</label>
          </div>
          <div className='form-check form-switch mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={formik.values.isRefundable}
              onChange={() => formik.setFieldValue('isRefundable', !formik.values.isRefundable)}
            />
            <label className='form-check-label'>Refundable</label>
          </div>
        </div>
        <div className='text-center pt-15'>
          <button type='reset' onClick={() => cancel()} className='btn btn-light me-3' disabled={formik.isSubmitting}>
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>
          <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting || isPaymentMethodLoading}>
            {intl.formatMessage({ id: 'HEADER.SAVE' })}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isPaymentMethodLoading) && <DataLoading />}
    </>
  );
};

export { PaymentMethodEditModalForm };
